import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { scrollToId } from "../hooks/useLenis";
import { SITE } from "../constants/site";

const LINKS = [
  { id: "catalogue", label: "Catalogue" },
  { id: "services", label: "Services" },
  { id: "guide", label: "Notre guide" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenu(false);
    if (pathname !== "/") { navigate("/"); setTimeout(() => scrollToId(id), 400); }
    else scrollToId(id);
  };

  const dark = !scrolled && pathname === "/";

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        dark ? "bg-transparent" : "bg-white/80 backdrop-blur-xl shadow-[0_1px_0_0_rgba(10,37,64,0.08)]"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <span className={`grid h-10 w-10 place-items-center font-display text-lg font-extrabold tracking-tight ${dark ? "bg-white text-navy" : "bg-navy text-white"}`}>
            IBM
          </span>
          <span className={`hidden sm:block font-display text-sm font-semibold leading-tight ${dark ? "text-white" : "text-navy"}`}>
            Ibn Bachir<br /><span className="font-normal opacity-70">Multimédia</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`underline-link text-sm font-medium ${dark ? "text-white/85 hover:text-white" : "text-navy/80 hover:text-navy"}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="cart-open-button"
            onClick={() => setOpen(true)}
            className={`relative flex h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors duration-300 ${
              dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-navy text-white hover:bg-royal"
            }`}
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Panier</span>
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  key={count}
                  data-testid="cart-count-badge"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="grid h-5 min-w-5 place-items-center rounded-full bg-gold px-1 text-[11px] font-bold text-navy"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button data-testid="mobile-menu-button" onClick={() => setMenu(!menu)} className={`md:hidden grid h-11 w-11 place-items-center ${dark ? "text-white" : "text-navy"}`}>
            {menu ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="md:hidden bg-white border-t border-navy/10 px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <button key={l.id} data-testid={`mobile-nav-link-${l.id}`} onClick={() => go(l.id)} className="text-left font-display text-lg font-semibold text-navy">{l.label}</button>
            ))}
            <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="text-sm text-slate-500">{SITE.phone} · {SITE.phone2}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
